// OWNER: AREA-OPS (dashboard, applications/*, suppressions, audit, system). Components go in src/features/servicebus.
// One Service Bus source: settings and mapping, waiting messages, the inbound log, and dead letters.
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { Cable, FlaskConical, PlugZap, Radio } from 'lucide-react'
import { useState } from 'react'
import { asApiError } from '@/api/errors'
import type { ConnectionTest, ServiceBusSource } from '@/api/types'
import { applicationQuery } from '@/api/queries/applications'
import { usePermissions } from '@/auth/usePermissions'
import { ConfirmDialog } from '@/components/ConfirmDialog'
import { EmptyState } from '@/components/EmptyState'
import { ModeBadge } from '@/components/ModeBadge'
import { ErrorState } from '@/components/QueryState'
import { RelativeTime } from '@/components/RelativeTime'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Skeleton, SkeletonText } from '@/components/ui/skeleton'
import { Switch } from '@/components/ui/switch'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Tooltip } from '@/components/ui/tooltip'
import { sourceQuery, useTestConnection, useUpdateSource } from '@/features/servicebus/api'
import { SourceCrumb } from '@/features/servicebus/crumbs'
import { DeadLettersPanel } from '@/features/servicebus/DeadLettersPanel'
import { InboundLogPanel } from '@/features/servicebus/InboundLogPanel'
import { InboundSheet } from '@/features/servicebus/InboundSheet'
import { entityLabel, shortNamespace, SourceStateBadge } from '@/features/servicebus/lib'
import { MappingPanel } from '@/features/servicebus/MappingPanel'
import { SourceDialog } from '@/features/servicebus/SourceDialog'
import { WaitingPanel } from '@/features/servicebus/WaitingPanel'

export const SOURCE_TABS = ['settings', 'waiting', 'log', 'dead-letters'] as const
export type SourceTab = (typeof SOURCE_TABS)[number]

interface SourceSearch {
  tab?: SourceTab
  /** An open inbound log entry. */
  entry?: string
}

export const Route = createFileRoute('/applications/$appId/service-bus/$sourceId')({
  staticData: { crumb: SourceCrumb },
  validateSearch: (s: Record<string, unknown>): SourceSearch => ({
    tab:
      SOURCE_TABS.includes(s.tab as SourceTab) && s.tab !== 'settings'
        ? (s.tab as SourceTab)
        : undefined,
    entry: typeof s.entry === 'string' && /^inb_\w+$/.test(s.entry) ? s.entry : undefined,
  }),
  loader: ({ context, params }) => {
    void context.queryClient.prefetchQuery(sourceQuery(params.sourceId))
  },
  component: SourcePage,
})

function SourcePage() {
  const { appId, sourceId } = Route.useParams()
  const { tab = 'settings', entry } = Route.useSearch()
  const navigate = useNavigate({ from: Route.fullPath })
  const q = useQuery(sourceQuery(sourceId))
  const [editing, setEditing] = useState(false)

  const setTab = (t: string) =>
    void navigate({
      search: (prev) => ({ ...prev, tab: t === 'settings' ? undefined : (t as SourceTab) }),
      replace: true,
    })
  const openEntry = (id: string | undefined) =>
    void navigate({ search: (prev) => ({ ...prev, entry: id }) })

  if (q.isError) {
    if (asApiError(q.error).status === 404)
      return (
        <EmptyState
          icon={Cable}
          title="Source not found"
          description="It may have been deleted."
          action={
            <Button asChild>
              <Link to="/applications/$appId/service-bus" params={{ appId }}>
                Back to sources
              </Link>
            </Button>
          }
        />
      )
    return <ErrorState error={q.error} onRetry={() => void q.refetch()} />
  }
  if (q.isPending)
    return (
      <div className="space-y-4">
        <Skeleton className="h-24 w-full" />
        <SkeletonText lines={8} />
      </div>
    )
  const source = q.data

  return (
    <div className="space-y-5">
      <SourceSummary source={source} />
      <Tabs value={tab} onValueChange={setTab}>
        <TabsList>
          <TabsTrigger value="settings">Settings</TabsTrigger>
          <TabsTrigger value="waiting">Waiting messages</TabsTrigger>
          <TabsTrigger value="log">Inbound log</TabsTrigger>
          <TabsTrigger value="dead-letters">Dead letters</TabsTrigger>
        </TabsList>
        <TabsContent value="settings" className="pt-4">
          <MappingPanel source={source} onEditConnection={() => setEditing(true)} />
        </TabsContent>
        <TabsContent value="waiting" className="pt-4">
          {tab === 'waiting' && <WaitingPanel source={source} onOpenEntry={openEntry} />}
        </TabsContent>
        <TabsContent value="log" className="pt-4">
          <InboundLogPanel sourceId={source.id} onOpenEntry={openEntry} activeEntry={entry} />
        </TabsContent>
        <TabsContent value="dead-letters" className="pt-4">
          {tab === 'dead-letters' && <DeadLettersPanel source={source} onOpenEntry={openEntry} />}
        </TabsContent>
      </Tabs>
      <InboundSheet
        source={source}
        entryId={entry}
        onOpenChange={(o) => !o && openEntry(undefined)}
        onOpenEntry={openEntry}
      />
      <SourceDialog appId={appId} source={source} open={editing} onOpenChange={setEditing} />
    </div>
  )
}

/** Name, state, entity, and the actions that apply to the whole source. */
function SourceSummary({ source }: { source: ServiceBusSource }) {
  const { can } = usePermissions()
  const update = useUpdateSource()
  const switchMode = useUpdateSource({
    successMessage: source.mode === 'test' ? 'Source is live' : 'Source switched to test',
  })
  const test = useTestConnection()
  const app = useQuery(applicationQuery(source.application_id))
  const [result, setResult] = useState<ConnectionTest | undefined>()
  const [confirmMode, setConfirmMode] = useState(false)
  const liveBlocked = source.mode === 'test' && app.data?.live_enabled === false

  const reason = source.state === 'error' ? source.last_error : source.state_reason

  return (
    <Card className="px-4 py-3.5">
      <div className="flex flex-wrap items-start gap-x-6 gap-y-3">
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="text-base font-semibold text-ink">{source.name}</h2>
            <ModeBadge mode={source.mode} />
            <SourceStateBadge source={source} />
          </div>
          <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-ink-2">
            <span className="font-mono text-xs">
              {shortNamespace(source.namespace)} / {entityLabel(source)}
            </span>
            <span>
              Last message{' '}
              {source.last_received_at ? <RelativeTime value={source.last_received_at} /> : 'never'}
            </span>
          </div>
          {source.description && <p className="mt-1 text-sm text-ink-2">{source.description}</p>}
          {reason && (
            <p
              className={
                source.state === 'error'
                  ? 'mt-2 text-sm whitespace-pre-line text-danger'
                  : 'mt-2 text-sm text-warning'
              }
            >
              {reason}
            </p>
          )}
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-4">
          {can('manage_config') && (
            <div className="flex items-center gap-2">
              <Switch
                id="sb-receive"
                checked={source.enabled}
                onCheckedChange={(enabled) => update.mutate({ id: source.id, body: { enabled } })}
              />
              <Label htmlFor="sb-receive">Receive</Label>
            </div>
          )}
          {can('edit_content') && (
            <Button
              size="sm"
              icon={<PlugZap />}
              loading={test.isPending}
              onClick={() => {
                setResult(undefined)
                void test.mutateAsync(source.id).then(setResult, (err) =>
                  setResult({
                    ok: false,
                    has_messages: false,
                    error: asApiError(err).detail ?? 'The test failed.',
                  }),
                )
              }}
            >
              Test connection
            </Button>
          )}
          {can('manage_config') &&
            (liveBlocked ? (
              <Tooltip content="Turn on Live sending in the application's Settings first.">
                <span>
                  <Button size="sm" icon={<Radio />} disabled>
                    Go live
                  </Button>
                </span>
              </Tooltip>
            ) : (
              <Button
                size="sm"
                variant={source.mode === 'test' ? 'primary' : 'secondary'}
                icon={source.mode === 'test' ? <Radio /> : <FlaskConical />}
                onClick={() => setConfirmMode(true)}
              >
                {source.mode === 'test' ? 'Go live' : 'Switch to test'}
              </Button>
            ))}
        </div>
      </div>
      <ConfirmDialog
        open={confirmMode}
        onOpenChange={setConfirmMode}
        title={source.mode === 'test' ? 'Take this source live?' : 'Switch this source to test?'}
        description={
          source.mode === 'test' ? (
            <>
              From the next message on <span className="font-mono">{entityLabel(source)}</span>,
              MailSmith sends real email to real recipients. Messages already captured as test
              aren’t resent.
            </>
          ) : (
            <>
              Messages become test messages and follow the application’s test mode (capture,
              allowlist, or redirect) instead of reaching real recipients.
            </>
          )
        }
        confirmLabel={source.mode === 'test' ? 'Go live' : 'Switch to test'}
        destructive={source.mode === 'test'}
        onConfirm={() =>
          switchMode.mutateAsync({
            id: source.id,
            body: { mode: source.mode === 'test' ? 'live' : 'test' },
          })
        }
      />
      {result && (
        <div
          role="status"
          className={
            result.ok
              ? 'mt-3 rounded-md border border-success/30 bg-success/10 px-3 py-2 text-sm text-success'
              : 'mt-3 rounded-md border border-danger/30 bg-danger-soft px-3 py-2 text-sm text-danger'
          }
        >
          {result.ok
            ? `Connected. MailSmith can read ${entityLabel(source)}${result.has_messages ? '; messages are waiting.' : '; nothing is waiting.'}`
            : result.error}
          {result.hint && <span className="mt-1 block text-ink">{result.hint}</span>}
        </div>
      )}
    </Card>
  )
}
